import { useMutation, useQueryClient } from "@tanstack/react-query";
import { placeMeal, type PlaceMealRequest } from "../api/meal-placement";
import { useRef } from "react";
import type { RecommendationAcceptanceMetadata } from "../recommendation-attribution";
import { useRecommendationFeedback } from "../providers/recommendation-feedback-provider";
import { ApiError } from "../api/client";
import { extractErrorStatus } from "../errors";
import type { MealPlan } from "../api/types";
import { MEAL_PLANS_QUERY_KEY, GROCERY_LISTS_QUERY_KEY } from "./query-keys";

function retainGroceryProjection(
  current: MealPlan | undefined,
  placed: MealPlan,
): MealPlan {
  return current?.groceryList
    ? { ...placed, groceryList: current.groceryList }
    : placed;
}

export type PlaceMealVariables = PlaceMealRequest & { attribution?: RecommendationAcceptanceMetadata; expectedActorUserId?: string; practicalFitRevision?: string };
export function usePlaceMeal() {
  const client = useQueryClient();
  const feedback = useRecommendationFeedback();
  const currentActor = (actor?: string) => feedback?.isCurrentActor?.() !== false && (!actor || feedback?.actorUserId === actor);
  const operations = useRef(new WeakMap<PlaceMealVariables, { actorUserId?: string; request: PlaceMealRequest; practicalFitRevision?: string }>());
  const capture = (variables: PlaceMealVariables) => {
    const { attribution, expectedActorUserId, practicalFitRevision, ...body } = variables;
    const operation = { actorUserId: expectedActorUserId ?? attribution?.expectedActorUserId ?? feedback?.actorUserId,
      practicalFitRevision, request: attribution || expectedActorUserId || practicalFitRevision ? body : variables };
    operations.current.set(variables, operation);
    return operation;
  };
  return useMutation({
    onMutate: capture,
    mutationFn: (variables: PlaceMealVariables) => {
      const operation = operations.current.get(variables) ?? capture(variables);
      if (!currentActor(operation.actorUserId) || (variables.attribution && variables.attribution.expectedActorUserId !== operation.actorUserId)) throw new ApiError("Signed-in account changed", 409);
      return operation.practicalFitRevision ? placeMeal(operation.request, variables.attribution, operation.actorUserId, operation.practicalFitRevision)
        : operation.actorUserId ? placeMeal(operation.request, variables.attribution, operation.actorUserId) : placeMeal(operation.request);
    },
    retry: (count, error) =>
      count < 1 &&
      (extractErrorStatus(error) === 0 || extractErrorStatus(error) >= 500),
    retryDelay: 0,
    onSuccess: ({ mealPlan }, _variables, context) => {
      if (!currentActor(context?.actorUserId)) return;
      client.setQueryData(
        [MEAL_PLANS_QUERY_KEY, mealPlan.id],
        (current: MealPlan | undefined) =>
          retainGroceryProjection(current, mealPlan),
      );
      client.setQueryData<MealPlan[]>([MEAL_PLANS_QUERY_KEY], (plans) =>
        plans
          ? [
              retainGroceryProjection(
                plans.find((plan) => plan.id === mealPlan.id),
                mealPlan,
              ),
              ...plans.filter((plan) => plan.id !== mealPlan.id),
            ]
          : plans,
      );
      // A successful write stays successful even if a subsequent read fails.
      void client.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
      void client.invalidateQueries({
        queryKey: [GROCERY_LISTS_QUERY_KEY, "meal-plan", mealPlan.id],
      });
    },
    onError: (_error, _variables, context) => {
      if (!currentActor(context?.actorUserId)) return;
      void client.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
    },
  });
}
